import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as flashActions from 'redux-flash'
import { Card } from 'components'
import { browserHistory, Link } from 'react-router'
import { LEARNING_HUB_ROUTE } from 'config'
import { selectors as globalSelectors } from '../../global-reducer'
import * as apiActions from 'api-actions'
import { createCourseAttempt } from '../data'
import LearningHubCourseBookmarkButton from '../../components/learning-hub-course-bookmark-button'
import { getLearningHubApiErrorMessage } from '../../utils/api-error-message'
import { Spinner } from 'lp-components'

const propTypes = {
  learningHubCatalog: PropTypes.object,
  myCourses: PropTypes.array,
  fetchLearningHubCourses: PropTypes.func.isRequired,
  fetchLearningHubCoursesMore: PropTypes.func.isRequired,
  fetchLearningHubMyCourses: PropTypes.func.isRequired,
  startLearningHubCourse: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
}

const defaultProps = {
  learningHubCatalog: undefined,
  myCourses: undefined,
}

function CourseCatalog({
  learningHubCatalog,
  myCourses,
  fetchLearningHubCourses,
  fetchLearningHubCoursesMore,
  fetchLearningHubMyCourses,
  startLearningHubCourse,
  flashErrorMessage,
}) {
  const [searchInput, setSearchInput] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedDiscipline, setSelectedDiscipline] = useState('all')
  const [searchCriteria, setSearchCriteria] = useState({
    query: '',
    category: 'all',
    discipline: 'all',
  })
  const [pendingLoads, setPendingLoads] = useState(() => new Set())

  useEffect(() => {
    if (myCourses === undefined) {
      fetchLearningHubMyCourses()
    }
  }, [myCourses, fetchLearningHubMyCourses])

  const startedCourseIds = useMemo(() => {
    if (!Array.isArray(myCourses)) return new Set()
    return new Set(
      myCourses
        .map((row) => Number(row?.course?.id))
        .filter((id) => !Number.isNaN(id))
    )
  }, [myCourses])

  const courseList = useMemo(
    () =>
      (learningHubCatalog?.disciplineGroups || []).flatMap(
        (g) => g.courses || []
      ),
    [learningHubCatalog]
  )

  const categories = useMemo(() => {
    const names = new Set()
    courseList.forEach((c) => {
      if (c.category) names.add(c.category)
    })
    return ['all', ...[...names].sort()]
  }, [courseList])

  const disciplines = useMemo(() => {
    const names = new Set()
    courseList.forEach((c) => {
      ;(c.disciplines || []).forEach((name) => {
        if (name) names.add(name)
      })
    })
    return ['all', ...[...names].sort()]
  }, [courseList])

  // Filter a single course against the active search criteria. Applied within
  // each discipline group's own courses (the server already groups a course
  // under each of its disciplines), so a multi-discipline course shows once per
  // row instead of being duplicated across rows.
  const matchesCriteria = useCallback(
    (course) => {
      const normalizedQuery = searchCriteria.query.trim().toLowerCase()
      const categoryMatch =
        searchCriteria.category === 'all' ||
        course.category === searchCriteria.category
      const disciplineMatch =
        searchCriteria.discipline === 'all' ||
        (course.disciplines || []).includes(searchCriteria.discipline)
      const title = (course.title || '').toLowerCase()
      const instructor = (course.instructor || '').toLowerCase()
      const category = (course.category || '').toLowerCase()
      const queryMatch =
        normalizedQuery.length === 0 ||
        title.includes(normalizedQuery) ||
        instructor.includes(normalizedQuery) ||
        category.includes(normalizedQuery)

      return categoryMatch && disciplineMatch && queryMatch
    },
    [searchCriteria]
  )

  const isGroupVisible = useCallback(
    (group) =>
      searchCriteria.discipline === 'all' ||
      group.disciplineName === searchCriteria.discipline,
    [searchCriteria]
  )

  const clearScrollLoadKey = (key) => {
    setPendingLoads((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  const handleDisciplineRowScroll = (group) => (event) => {
    const el = event.currentTarget
    const threshold = 64
    if (el.scrollLeft + el.clientWidth < el.scrollWidth - threshold) return
    if (group.courses.length >= group.totalCount) return

    const key = group.uncategorized ? 'u' : String(group.disciplineId)
    if (pendingLoads.has(key)) return

    setPendingLoads((prev) => new Set(prev).add(key))

    const promise = fetchLearningHubCoursesMore({
      disciplineId: group.disciplineId,
      uncategorized: Boolean(group.uncategorized),
      offset: group.courses.length,
      limit: 5,
      query: searchCriteria.query,
    })

    if (promise && typeof promise.finally === 'function') {
      promise.finally(() => clearScrollLoadKey(key))
    } else {
      window.setTimeout(() => clearScrollLoadKey(key), 500)
    }
  }

  if (learningHubCatalog === undefined) {
    return (
      <div className="content-block-container learning-hub-course-catalog">
        <Spinner />
      </div>
    )
  }

  const disciplineGroups = learningHubCatalog.disciplineGroups || []

  return (
    <div className="content-block-container learning-hub-course-catalog">
      <header className="learning-hub-course-catalog__header">
        <h1>Course Catalog</h1>
      </header>

      <Card className="learning-hub-course-catalog__search-card">
        <section className="learning-hub-course-catalog__controls">
          <h2>What do you want to learn?</h2>
          <form
            className="learning-hub-course-catalog__search-row"
            onSubmit={(event) => {
              event.preventDefault()
              const query = searchInput.trim()
              setSearchCriteria({
                query,
                category: selectedCategory,
                discipline: selectedDiscipline,
              })
              fetchLearningHubCourses({ query })
            }}
          >
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by Course Name, Instructor, or Category..."
              aria-label="Search courses"
            />
            <button type="submit" className="button-primary">
              Search
            </button>
          </form>

          <div className="learning-hub-course-catalog__filters">
            <label>
              <span>Category</span>
              <select
                value={selectedCategory}
                onChange={(event) => {
                  const value = event.target.value
                  setSelectedCategory(value)
                  setSearchCriteria((prev) => ({
                    ...prev,
                    category: value,
                  }))
                }}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Discipline</span>
              <select
                value={selectedDiscipline}
                onChange={(event) => {
                  const value = event.target.value
                  setSelectedDiscipline(value)
                  setSearchCriteria((prev) => ({
                    ...prev,
                    discipline: value,
                  }))
                }}
              >
                {disciplines.map((discipline) => (
                  <option key={discipline} value={discipline}>
                    {discipline === 'all' ? 'All Disciplines' : discipline}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>
      </Card>

      <section className="learning-hub-course-catalog__results">
        {!disciplineGroups.some(
          (group) =>
            isGroupVisible(group) && (group.courses || []).some(matchesCriteria)
        ) && (
          <Card>
            <p>
              No courses found. Try searching by a different course name,
              instructor, or category, or adjust your filters.
            </p>
          </Card>
        )}

        {disciplineGroups.map((group) => {
          // When a specific discipline is selected, only render that row so a
          // multi-discipline course doesn't also surface under its other rows.
          if (!isGroupVisible(group)) return null

          const rowCourses = (group.courses || []).filter(matchesCriteria)
          if (rowCourses.length === 0) return null

          return (
            <div
              key={group.uncategorized ? 'uncategorized' : group.disciplineId}
              className="learning-hub-course-catalog__group"
            >
              <h3>{group.disciplineName}</h3>
              <div
                className="learning-hub-course-catalog__row-scroll"
                onScroll={handleDisciplineRowScroll(group)}
              >
                {rowCourses.map((course) => {
                  const alreadyEnrolled = startedCourseIds.has(
                    Number(course.id)
                  )
                  return (
                    <Card
                      key={course.id}
                      className="learning-hub-course-card-shell"
                    >
                      <article className="learning-hub-course-card">
                        <Link
                          to={`${LEARNING_HUB_ROUTE}/course/${course.id}`}
                          className="learning-hub-course-card__course-link"
                        >
                          <img
                            src={course.coverImage}
                            alt={`${course.title} cover`}
                            className="learning-hub-course-card__cover"
                          />
                          <h4>{course.title}</h4>
                          {course.instructor ? (
                            <p className="learning-hub-course-card__instructor">
                              {course.instructor}
                            </p>
                          ) : null}
                        </Link>
                        <div className="learning-hub-course-card__actions">
                          <button
                            type="button"
                            className="button-primary button-small"
                            onClick={async () => {
                              if (alreadyEnrolled) {
                                try {
                                  await startLearningHubCourse(course.id)
                                } catch (error) {
                                  flashErrorMessage(
                                    getLearningHubApiErrorMessage(
                                      error,
                                      'Unable to resume this course.'
                                    )
                                  )
                                  return
                                }
                                createCourseAttempt(course.id)
                                browserHistory.push(
                                  `${LEARNING_HUB_ROUTE}/course/${course.id}/chapters`
                                )
                                return
                              }
                              try {
                                await startLearningHubCourse(course.id)
                                createCourseAttempt(course.id)
                                fetchLearningHubMyCourses()
                                browserHistory.push(
                                  `${LEARNING_HUB_ROUTE}/course/${course.id}/chapters`
                                )
                              } catch (error) {
                                flashErrorMessage(
                                  getLearningHubApiErrorMessage(
                                    error,
                                    'Unable to start this course.'
                                  )
                                )
                              }
                            }}
                          >
                            {alreadyEnrolled ? 'Continue' : 'Take Course'}
                          </button>
                          <LearningHubCourseBookmarkButton
                            courseId={course.id}
                            courseTitle={course.title}
                          />
                        </div>
                      </article>
                    </Card>
                  )
                })}
              </div>
            </div>
          )
        })}
      </section>
    </div>
  )
}

CourseCatalog.propTypes = propTypes
CourseCatalog.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    learningHubCatalog: globalSelectors.learningHubCatalog(state),
    myCourses: globalSelectors.myCourses(state),
  }
}

const mapDispatchToProps = {
  fetchLearningHubCourses: apiActions.fetchLearningHubCourses,
  fetchLearningHubCoursesMore: apiActions.fetchLearningHubCoursesMore,
  fetchLearningHubMyCourses: apiActions.fetchLearningHubMyCourses,
  startLearningHubCourse: apiActions.startLearningHubCourse,
  flashErrorMessage: flashActions.flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CourseCatalog
)
